import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Card, Button, PageHeader, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { PermissionGate } from "@/components/PermissionGate";
import {
  useGenerationLog,
  useGenerationLogs,
  type GenerationLogFilters,
} from "@/hooks/useGenerationLogs";
import type {
  ContentType,
  GenerationStatus,
  LogRow,
} from "@/types/content";

const CONTENT_TYPE_OPTIONS: Array<{ value: ContentType | "all"; label: string }> = [
  { value: "all", label: "All content types" },
  { value: "product_caption", label: "Product caption" },
  { value: "motivational_nudge", label: "Motivational nudge" },
  { value: "voice_message", label: "Voice message" },
  { value: "gamified_prompt", label: "Gamified prompt" },
];

const STATUS_OPTIONS: Array<{ value: GenerationStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "ok", label: "OK" },
  { value: "error", label: "Error" },
  { value: "blocked", label: "Blocked" },
];

const STATUS_BADGE_VARIANT: Record<GenerationStatus, "success" | "error" | "warning"> = {
  ok: "success",
  error: "error",
  blocked: "warning",
};

const PAGE_SIZE = 25;

const truncate = (text: string | null | undefined, max = 80): string => {
  if (!text) return "—";
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max).trimEnd() + "…";
};

interface LogDrawerProps {
  logId: number | null;
  onClose: () => void;
}

const LogDrawer = ({ logId, onClose }: LogDrawerProps) => {
  const { data: log, isLoading } = useGenerationLog(logId ?? -1);
  if (logId === null) return null;

  return (
    <>
      <div className="dw-ov open" onClick={onClose} />
      <aside className="dw open">
        <div className="dw-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3>
              Generation Log{logId !== null ? ` #${logId}` : ""}
              {log && (
                <Badge variant={STATUS_BADGE_VARIANT[log.status]}>
                  <span className="dot" />
                  {log.status}
                </Badge>
              )}
            </h3>
            {log && <p>{format(new Date(log.created_at), "PPpp")}</p>}
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <Icon name="x" />
          </button>
        </div>

        <div className="dw-b">
          {isLoading && (
            <div className="dsec">
              <p className="meta">Loading...</p>
            </div>
          )}

          {!isLoading && log && (
            <>
              <dl className="dg">
                <div>
                  <dt>Template</dt>
                  <dd className="mono">
                    {log.template_slug ?? "—"}
                    {log.template_id !== null && log.template_id !== undefined && (
                      <span style={{ color: "var(--ink-4)" }}> #{log.template_id}</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <Badge variant={STATUS_BADGE_VARIANT[log.status]}>
                      <span className="dot" />
                      {log.status}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt>Content</dt>
                  <dd>
                    {log.content_type} ·{" "}
                    <span className="mono" style={{ textTransform: "uppercase" }}>
                      {log.locale}
                    </span>{" "}
                    · {log.audience_role}
                  </dd>
                </div>
                <div>
                  <dt>Requester</dt>
                  <dd className="mono">
                    {log.requester_id ?? "anonymous"}{" "}
                    <span style={{ color: "var(--ink-4)", fontFamily: "var(--sans)" }}>
                      {log.requester_kind}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Provider / Model</dt>
                  <dd>
                    {log.provider ?? "—"} ·{" "}
                    <span className="mono">{log.chat_model ?? "—"}</span>
                  </dd>
                </div>
                <div>
                  <dt>Latency · Tokens</dt>
                  <dd className="num">
                    {log.latency_ms ?? "—"} ms · {log.tokens.prompt} +{" "}
                    {log.tokens.output}
                  </dd>
                </div>
              </dl>

              {log.error_message && (
                <div className="dsec">
                  <div className="dsec-h">
                    <b>Error</b>
                  </div>
                  <div
                    style={{
                      background: "var(--err-bg)",
                      color: "var(--err)",
                      border: "1px solid var(--err-bd)",
                      borderRadius: "var(--r-sm)",
                      padding: "10px 12px",
                      fontSize: "12.5px",
                      lineHeight: 1.5,
                    }}
                  >
                    {log.error_message}
                  </div>
                </div>
              )}

              <div className="dsec">
                <div className="dsec-h">
                  <b>Output Text</b>
                </div>
                <pre className="code" style={{ fontFamily: "var(--sans)", fontSize: "12.5px" }}>
                  {log.output_text || "—"}
                </pre>
              </div>

              {log.audio_object_key && (
                <div className="dsec">
                  <div className="dsec-h">
                    <b>Audio</b>
                  </div>
                  <audio
                    controls
                    src={`/api/content/voice/${log.id}`}
                    style={{ width: "100%" }}
                  />
                  <p className="hint" style={{ marginTop: "7px" }}>
                    Object key <span className="mono">{log.audio_object_key}</span>
                  </p>
                </div>
              )}

              <div className="dsec">
                <div className="dsec-h">
                  <b>Inputs</b>
                </div>
                <pre className="code">{JSON.stringify(log.inputs, null, 2)}</pre>
              </div>

              {log.citations.length > 0 && (
                <div className="dsec">
                  <div className="dsec-h">
                    <b>Citations</b>
                    <span className="r">
                      <span className="b def">{log.citations.length}</span>
                    </span>
                  </div>
                  {log.citations.map((c, idx) => (
                    <div
                      key={`${c.document_id ?? "anon"}-${idx}`}
                      className="cit"
                      style={idx === log.citations.length - 1 ? { marginBottom: 0 } : undefined}
                    >
                      <div className="cit-h">
                        <b className="mono">
                          {c.source ?? c.document_id ?? "Source"}
                        </b>
                        {typeof c.score === "number" && (
                          <span className="sc">{c.score.toFixed(2)}</span>
                        )}
                      </div>
                      {c.snippet && <p>{c.snippet}</p>}
                    </div>
                  ))}
                </div>
              )}

              {log.prompt_snapshot && (
                <div className="dsec">
                  <div className="dsec-h">
                    <b>Prompt Snapshot</b>
                  </div>
                  <pre className="code">{log.prompt_snapshot}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
};

const GenerationLogs = () => {
  const [contentType, setContentType] = useState<ContentType | "all">("all");
  const [status, setStatus] = useState<GenerationStatus | "all">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [activeLogId, setActiveLogId] = useState<number | null>(null);

  const filters = useMemo<GenerationLogFilters>(() => {
    const f: GenerationLogFilters = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (contentType !== "all") f.content_type = contentType;
    if (status !== "all") f.status = status;
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    return f;
  }, [contentType, status, dateFrom, dateTo, page]);

  const { data, isLoading } = useGenerationLogs(filters);
  const items: LogRow[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleClearFilters = () => {
    setContentType("all");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <div className="page wide">
      <PageHeader
        label="Content Generation"
        title="Generation Logs"
        description="Every content generation, including failures, with full context."
      />

      <PermissionGate resource="generation_logs" action="view">
        {/* Filters */}
        <Card>
          <div className="row c5" style={{ alignItems: "end" }}>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">Content type</label>
              <select
                className="sel"
                value={contentType}
                onChange={(e) => {
                  setContentType(e.target.value as ContentType | "all");
                  setPage(1);
                }}
              >
                {CONTENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">Status</label>
              <select
                className="sel"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as GenerationStatus | "all");
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">From</label>
              <input
                className="inp"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">To</label>
              <input
                className="inp"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
                <Icon name="refresh" />
                Clear filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <div className="card">
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Time</th>
                  <th style={{ width: "16%" }}>Template</th>
                  <th>Type / Locale / Audience</th>
                  <th>Requester</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 90 }}>Latency</th>
                  <th>Output preview</th>
                  <th style={{ width: 60, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveLogId(row.id)}
                  >
                    <td>
                      <div className="num" style={{ fontWeight: 600, color: "var(--ink)" }}>
                        {format(new Date(row.created_at), "HH:mm:ss")}
                      </div>
                      <div className="sub-cell" style={{ fontFamily: "var(--sans)" }}>
                        {format(new Date(row.created_at), "MMM d")}
                      </div>
                    </td>
                    <td>
                      <div
                        className="mono"
                        style={{ fontWeight: 600, color: "var(--ink)", fontSize: 12 }}
                      >
                        {row.template_slug ?? "—"}
                      </div>
                    </td>
                    <td>
                      {row.content_type} ·{" "}
                      <span
                        className="mono"
                        style={{ textTransform: "uppercase", fontWeight: 600 }}
                      >
                        {row.locale}
                      </span>{" "}
                      · {row.audience_role}
                    </td>
                    <td>
                      <div
                        className="mono"
                        style={{ fontSize: 12, color: "var(--ink)" }}
                      >
                        {row.requester_id ?? "—"}
                      </div>
                      <div className="sub-cell" style={{ fontFamily: "var(--sans)" }}>
                        {row.requester_kind}
                      </div>
                    </td>
                    <td>
                      <Badge variant={STATUS_BADGE_VARIANT[row.status]}>
                        <span className="dot" />
                        {row.status}
                      </Badge>
                    </td>
                    <td className="num">{row.latency_ms ?? "—"} ms</td>
                    <td style={{ color: "var(--ink-3)", fontSize: "12.5px" }}>
                      {truncate(row.output_text, 80)}
                    </td>
                    <td>
                      <div className="acts">
                        <button
                          type="button"
                          className="act"
                          title="View"
                          aria-label="View"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveLogId(row.id);
                          }}
                        >
                          <Icon name="eye" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!items.length && !isLoading && (
                  <tr>
                    <td colSpan={8}>
                      <div className="empty sm">
                        <div className="empty-i">
                          <Icon name="list" />
                        </div>
                        <b>No generation logs</b>
                        <p>No generation logs match these filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ textAlign: "center", color: "var(--ink-4)", padding: "28px 0" }}
                    >
                      Loading logs...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="pg">
            <span className="info">
              Page <b>{page}</b> of <b>{totalPages}</b> ·{" "}
              <b>{total.toLocaleString()}</b> total
            </span>
            <div className="r">
              <button
                type="button"
                className="pgb"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <Icon name="l" />
                Previous
              </button>
              <button
                type="button"
                className="pgb"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
                <Icon name="r" />
              </button>
            </div>
          </div>
        </div>
      </PermissionGate>

      <LogDrawer logId={activeLogId} onClose={() => setActiveLogId(null)} />
    </div>
  );
};

export default GenerationLogs;
